/**
 * Delivery Dashboard — Sprint 3 Step 3.2 (Frontend)
 *
 * Logistics view: on-time %, avg delay days, supplier performance,
 * delivery completion %, recent deliveries flat list.
 *
 * Endpoints:
 *   GET /api/pece/deliveries/summary?days=N
 *   GET /api/pece/deliveries/log
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Truck, RefreshCw, Loader2, Building, Clock, TrendingUp,
  PackageOpen, AlertTriangle, CheckCircle2, ArrowRight,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;

const DeliveryDashboard = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [s, l] = await Promise.all([
          axiosInstance.get(`/pece/deliveries/summary?days=${windowDays}`),
          axiosInstance.get(`/pece/deliveries/log?limit=50`),
        ]);
        if (!cancelled) {
          setSummary(s.data);
          setLog(l.data);
        }
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل بيانات التسليمات');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [windowDays, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const onTimeColor = useMemo(() => {
    const p = summary?.on_time_pct ?? 0;
    if (p >= 90) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (p >= 70) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-rose-700 bg-rose-50 border-rose-200';
  }, [summary]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="delivery-dashboard-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-cyan-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Delivery Dashboard · Step 3.2
              </div>
              <h1 className="text-2xl font-bold text-gray-900">لوحة تتبع التسليمات اللوجستية</h1>
              <p className="text-sm text-gray-500 mt-1">
                أداء الموردين · نسبة الالتزام بالوقت · معدل التأخير · سجل آخر الشحنات
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="delivery-window-select"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value={7}>آخر 7 أيام</option>
                <option value={30}>آخر 30 يوماً</option>
                <option value={90}>آخر 90 يوماً</option>
                <option value={365}>آخر سنة</option>
              </select>
              <button
                data-testid="delivery-refresh-btn"
                onClick={reload}
                className="px-4 py-2 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 flex items-center gap-1 font-semibold text-sm"
              >
                <RefreshCw className="w-4 h-4" /> تحديث
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="delivery-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && summary && (
          <>
            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="delivery-kpi-strip">
              <KPI
                testId="kpi-on-time"
                icon={<Clock className="w-5 h-5" />}
                label="الالتزام بالوقت"
                value={fmtPct(summary.on_time_pct)}
                cls={onTimeColor}
              />
              <KPI
                testId="kpi-avg-delay"
                icon={<AlertTriangle className="w-5 h-5" />}
                label="متوسط التأخير"
                value={`${summary.avg_delay_days.toFixed(1)} يوم`}
                cls="text-amber-700 bg-amber-50 border-amber-200"
              />
              <KPI
                testId="kpi-completion"
                icon={<TrendingUp className="w-5 h-5" />}
                label="نسبة الاكتمال"
                value={fmtPct(summary.avg_completion_pct)}
                cls="text-cyan-700 bg-cyan-50 border-cyan-200"
              />
              <KPI
                testId="kpi-deliveries"
                icon={<PackageOpen className="w-5 h-5" />}
                label="إجمالي الشحنات"
                value={String(summary.total_deliveries)}
                sub={`${summary.over_delivery_count} تجاوز كمية`}
                cls="text-indigo-700 bg-indigo-50 border-indigo-200"
              />
            </div>

            {/* Top Suppliers */}
            <Section
              title="أعلى الموردين أداءً"
              icon={<Building className="w-5 h-5 text-gray-500" />}
              testId="delivery-top-suppliers"
            >
              {summary.top_suppliers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  لم يُسجَّل أي مورد بعد. قم بربط supplier_name بـ MIR ثم سجل شحنة.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-600 font-bold">
                    <tr>
                      <th className="px-3 py-2 text-right">المورد</th>
                      <th className="px-3 py-2 text-right">عدد الشحنات</th>
                      <th className="px-3 py-2 text-right">عدد MIRs</th>
                      <th className="px-3 py-2 text-right">متوسط الاكتمال</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summary.top_suppliers.map((s) => (
                      <tr key={s.supplier_name} data-testid={`supplier-row-${s.supplier_name}`}
                          className="hover:bg-cyan-50/30">
                        <td className="px-3 py-2 font-semibold">{s.supplier_name}</td>
                        <td className="px-3 py-2 font-mono">{s.deliveries}</td>
                        <td className="px-3 py-2 font-mono">{s.mir_count}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            s.avg_completion_pct >= 95 ? 'bg-emerald-100 text-emerald-700' :
                            s.avg_completion_pct >= 75 ? 'bg-amber-100 text-amber-700' :
                            'bg-rose-100 text-rose-700'
                          }`}>
                            {fmtPct(s.avg_completion_pct)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Recent deliveries */}
            <Section
              title="آخر الشحنات"
              icon={<Truck className="w-5 h-5 text-gray-500" />}
              testId="delivery-recent-log"
            >
              {log.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  لم تُسجَّل أي شحنة بعد. افتح MIR → اضغط &laquo;Log Delivery&raquo;.
                </div>
              ) : (
                <div className="divide-y" data-testid="delivery-log-list">
                  {log.slice(0, 20).map((d) => (
                    <div key={`${d.mir_id}-${d.delivery_no}`} className="p-3 hover:bg-cyan-50/30" data-testid={`delivery-entry-${d.delivery_no}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-mono font-bold text-sm">{d.delivery_no}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            MIR: <span className="font-mono">{d.mir_no}</span>
                            {d.supplier_name && <> · مورد: <strong>{d.supplier_name}</strong></>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-700">
                            وصول: {fmtDate(d.delivered_at)}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5 justify-end">
                            {d.over_delivery && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                تجاوز
                              </span>
                            )}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              (d.delivery_completion_pct || 0) >= 100 ?
                                'bg-emerald-100 text-emerald-700' :
                                'bg-cyan-100 text-cyan-700'
                            }`}>
                              {fmtPct(d.delivery_completion_pct)}
                            </span>
                            {d.correlation_id && (
                              <a
                                href={`/ecp?correlation_id=${d.correlation_id}`}
                                className="text-[10px] text-indigo-600 underline ml-1"
                              >
                                ECP
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      {d.over_delivery_reason && (
                        <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded">
                          <strong>سبب التجاوز:</strong> {d.over_delivery_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <div className="bg-white rounded-xl border border-dashed border-cyan-300 p-5 text-center">
              <CheckCircle2 className="w-8 h-8 mx-auto text-cyan-500 mb-2" />
              <p className="text-sm text-gray-600">
                لتسجيل شحنة جديدة، انتقل إلى{' '}
                <a href="/pece/mir" className="text-cyan-700 font-bold underline inline-flex items-center gap-1">
                  قائمة MIR <ArrowRight className="w-3 h-3" />
                </a>{' '}
                واضغط &laquo;تسجيل تسليم&raquo; على أي MIR.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const KPI = ({ testId, icon, label, value, sub, cls }) => (
  <div data-testid={testId} className={`rounded-xl border p-4 ${cls}`}>
    <div className="flex items-center gap-2 text-xs uppercase font-bold opacity-80">
      {icon}{label}
    </div>
    <div className="text-2xl font-bold mt-2">{value}</div>
    {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
  </div>
);

const Section = ({ title, icon, children, testId }) => (
  <div className="bg-white rounded-xl border" data-testid={testId}>
    <div className="px-4 py-3 border-b flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-bold text-gray-800">{title}</h3>
    </div>
    <div>{children}</div>
  </div>
);

export default DeliveryDashboard;
